import { SingleSelectChecklist } from '@/components/SingleSelectChecklist';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AnswerResponse } from '@/dtos/Answers/index';
import { BaseAssessment, BaseAssessmentItem } from '@/dtos/Assessments/index';
import { BaseAssessmentExchange, BasePerson, Step2Request } from '@/dtos/FlowClinicalSessions/index';
import { AssessmentRule } from '@/dtos/Rules';
import { useGetAssessmentRules, useGetAssessments } from '@/hooks/Assessment';
import { useProcessStep2 } from '@/hooks/FlowClinical';
import { AlertCircle, ClipboardList, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Step2Props {
  sessionId: string;
  onSuccess: (sessionId: string) => void;
  onBack: () => void;
  assessmentExchanges?: BaseAssessmentExchange[];
  person?: BasePerson;
}

type InputType = 'string' | 'number' | 'bool' | 'checkList';

export default function Step2({ sessionId, onSuccess, onBack, assessmentExchanges, person }: Step2Props) {
  const { data: assessmentsResponse, isLoading: isLoadingAssessments } = useGetAssessments();
  const { data: rulesResponse } = useGetAssessmentRules();
  const processStep2Mutation = useProcessStep2();

  const assessments = assessmentsResponse?.isSuccess ? assessmentsResponse.data || [] : [];
  const assessmentRules = rulesResponse?.isSuccess ? rulesResponse.data || {} : {};

  // State: Selected assessment IDs
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<string[]>([]);

  // State: Answers for each assessment item
  // Key: assessmentItemId (string), Value: AnswerResponse
  const [assessmentAnswers, setAssessmentAnswers] = useState<Record<string, AnswerResponse>>({});

  // State: Validation errors for items
  // Key: assessmentItemId (string), Value: error message (string)
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  const getAssessmentItemById = (itemId: string): BaseAssessmentItem | undefined => {
    for (const assessment of assessments) {
      const found = assessment.items?.find((item) => item.id === itemId);
      if (found) return found;
    }
    return undefined;
  };

  /**
   * Resolves profile key from assessment item or item exchange.
   * Prefers registryTermSnapshot (from exchange), then answerPersonProfileSnapshot, then item definition.
   */
  const getProfileKeyForLookup = (
    item: BaseAssessmentItem,
    itemExchange?: { registryTermSnapshot?: string; answerPersonProfileSnapshot?: string },
  ): string | undefined => {
    const key =
      itemExchange?.registryTermSnapshot?.trim() ||
      itemExchange?.answerPersonProfileSnapshot?.trim() ||
      item.registryTerm?.trim() ||
      item.answerPersonProfile?.trim();
    return key || undefined;
  };

  const getItemRuleKeys = (item: BaseAssessmentItem): string[] => {
    const keys = [item.registryTerm, item.answerPersonProfile, item.question]
      .map((value) => value?.trim())
      .filter((value): value is string => !!value);
    return keys;
  };

  /**
   * Gets value from person profile or healthProfile when the given key matches a property.
   * Matches registryTermSnapshot / answerPersonProfile keys against person and healthProfile fields.
   */
  const getPersonProfileValue = (profileKey: string | undefined): string | number | boolean | undefined => {
    if (!profileKey || !person) return undefined;

    const normalized = profileKey.trim().toLowerCase();

    // Person (profile) direct properties
    if (normalized === 'age') return person.age;
    if (normalized === 'gender') return person.gender;
    if (normalized === 'country') return person.country;
    if (normalized === 'region') return person.country; // region often maps to country
    if (normalized === 'birthdate') return person.birthDate ? String(person.birthDate) : undefined;

    // HealthProfile properties
    const hp = person.healthProfile;
    if (!hp) return undefined;

    if (normalized === 'issmoker' || normalized === 'smoking') return hp.isSmoker;
    if (normalized === 'isformersmoker') return hp.isFormerSmoker;
    if (normalized === 'hasdiabetes' || normalized === 'hasdiabets') return hp.hasDiabetes;
    if (normalized === 'hashypertension') return hp.hasHypertension;
    if (normalized === 'hasdyslipidemia') return hp.hasDyslipidemia;
    if (normalized === 'bodymassindex' || normalized === 'bmi') return hp.bodyMassIndex;
    if (normalized === 'systolicbloodpressure' || normalized === 'sbp') return hp.systolicBloodPressure;
    if (normalized === 'familyhistorycardiovasculardisease') return hp.familyHistoryCardiovascularDisease;

    return undefined;
  };

  const getSuggestedAnswer = (
    item: BaseAssessmentItem,
    itemExchange?: { registryTermSnapshot?: string; answerPersonProfileSnapshot?: string },
  ): AnswerResponse | undefined => {
    const profileKey = getProfileKeyForLookup(item, itemExchange);
    const profileValue = getPersonProfileValue(profileKey);
    if (profileValue === undefined || profileValue === null) return undefined;

    const normalize = (value?: string) => (value ?? '').trim().toLowerCase();
    const boolOptions = item.answers || [];
    const rawValue = String(profileValue);

    let displayValue = rawValue;
    if (typeof profileValue === 'boolean' && boolOptions.length > 0) {
      const trueOptions = ['da', 'true', 'yes', '1'];
      const falseOptions = ['nu', 'false', 'no', '0'];
      const match = boolOptions.find((opt) =>
        profileValue
          ? trueOptions.includes(normalize(opt.displayValue))
          : falseOptions.includes(normalize(opt.displayValue)),
      );
      displayValue = match?.displayValue ?? rawValue;
    } else if (boolOptions.length > 0) {
      const match = boolOptions.find((opt) => normalize(opt.displayValue) === normalize(rawValue));
      displayValue = match?.displayValue ?? rawValue;
    }

    return {
      displayValue,
      submittedValue: displayValue,
    };
  };

  const hasSubmittedValue = (answer?: AnswerResponse) =>
    answer?.submittedValue !== undefined && answer?.submittedValue !== '';

  const normalizeRuleKey = (value?: string) =>
    value ? value.trim().toLowerCase().replace(/[^a-z0-9]/g, '') : '';

  const normalizedRules = useMemo(() => {
    const map = new Map<string, AssessmentRule>();
    Object.entries(assessmentRules).forEach(([key, rule]) => {
      const normalized = normalizeRuleKey(key);
      if (normalized) {
        map.set(normalized, rule);
      }
    });
    return map;
  }, [assessmentRules]);

  const isWithinOneEdit = (a: string, b: string): boolean => {
    if (a === b) return true;
    const lengthDiff = Math.abs(a.length - b.length);
    if (lengthDiff > 1) return false;

    if (a.length === b.length) {
      let diffCount = 0;
      for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) diffCount += 1;
        if (diffCount > 1) return false;
      }
      return true;
    }

    const shorter = a.length < b.length ? a : b;
    const longer = a.length < b.length ? b : a;
    let i = 0;
    let j = 0;
    let skipped = false;
    while (i < shorter.length && j < longer.length) {
      if (shorter[i] === longer[j]) {
        i += 1;
        j += 1;
        continue;
      }
      if (skipped) return false;
      skipped = true;
      j += 1;
    }
    return true;
  };

  const getAssessmentRule = (assessment?: BaseAssessment): AssessmentRule | undefined => {
    if (!assessment?.name) return undefined;
    const normalized = normalizeRuleKey(assessment.name);
    if (!normalized) return undefined;
    const exact = normalizedRules.get(normalized);
    if (exact) return exact;

    let fallback: AssessmentRule | undefined;
    normalizedRules.forEach((rule, key) => {
      if (!fallback && isWithinOneEdit(key, normalized)) {
        fallback = rule;
      }
    });

    return fallback;
  };

  const isTruthyAnswer = (answer?: AnswerResponse): boolean => {
    const raw = (answer?.submittedValue || answer?.displayValue || '').trim().toLowerCase();
    return ['da', 'true', 'yes', '1'].includes(raw);
  };

  const matchesRuleKey = (ruleKey: string, itemKey: string): boolean => {
    if (!ruleKey || !itemKey) return false;
    return (
      itemKey === ruleKey ||
      itemKey.includes(ruleKey) ||
      ruleKey.includes(itemKey) ||
      isWithinOneEdit(itemKey, ruleKey)
    );
  };

  const isItemVisible = (assessment: BaseAssessment, item: BaseAssessmentItem): boolean => {
    const rule = getAssessmentRule(assessment);
    const displayAnswer = rule?.displayAnswer;
    let displayOk = true;

    const itemKeys = getItemRuleKeys(item).map((key) => normalizeRuleKey(key)).filter(Boolean);
    if (itemKeys.length === 0) return true;

    if (displayAnswer && Object.keys(displayAnswer).length > 0) {
      for (const [triggerKey, dependentValue] of Object.entries(displayAnswer)) {
        const normalizedTrigger = normalizeRuleKey(triggerKey);

        if (dependentValue && !Array.isArray(dependentValue)) {
          if (normalizedTrigger !== 'model') {
            continue;
          }

          const modelItem = assessment.items?.find(
            (candidate) =>
              getItemRuleKeys(candidate).some((key) => matchesRuleKey('model', normalizeRuleKey(key))),
          );
          const modelRaw =
            modelItem &&
            (getItemAnswer(modelItem.id)?.submittedValue ||
              getItemAnswer(modelItem.id)?.displayValue);
          const selectedModel = normalizeRuleKey(modelRaw || 'base');

          if (itemKeys.some((key) => matchesRuleKey('model', key))) {
            return displayOk;
          }

          const modelKey = Object.keys(dependentValue).find(
            (key) => matchesRuleKey(selectedModel, normalizeRuleKey(key)),
          );

          const allowed = (modelKey ? dependentValue[modelKey] : [])
            .map((value) => normalizeRuleKey(value))
            .filter(Boolean);

          const conditionalKeys = new Set<string>();
          Object.values(dependentValue).forEach((values) => {
            (values || []).forEach((value) => {
              const normalized = normalizeRuleKey(value);
              if (normalized) {
                conditionalKeys.add(normalized);
              }
            });
          });

          if (!itemKeys.some((key) => Array.from(conditionalKeys).some((cond) => matchesRuleKey(cond, key)))) {
            return displayOk;
          }

          return displayOk && itemKeys.some((key) => allowed.some((allowedKey) => matchesRuleKey(allowedKey, key)));
        }

        const dependentKeys = (dependentValue || [])
          .map((value) => normalizeRuleKey(value))
          .filter(Boolean);

        if (normalizedTrigger && itemKeys.some((key) => matchesRuleKey(normalizedTrigger, key))) {
          displayOk = true;
          continue;
        }

        if (itemKeys.some((key) => dependentKeys.some((dep) => matchesRuleKey(dep, key)))) {
          const triggerItem = assessment.items?.find(
            (candidate) =>
              getItemRuleKeys(candidate).some((key) => matchesRuleKey(normalizedTrigger, normalizeRuleKey(key))),
          );
          if (triggerItem && isTruthyAnswer(getItemAnswer(triggerItem.id))) {
            displayOk = true;
          } else {
            displayOk = false;
          }
        }
      }
    }

    return displayOk;
  };

  // Load existing assessment exchanges when navigating back from Step3
  useEffect(() => {
    if (assessmentExchanges && assessmentExchanges.length > 0) {
      // Extract assessment IDs
      const assessmentIds = assessmentExchanges.map((exchange) => exchange.assessmentId);
      setSelectedAssessmentIds(assessmentIds);

      // Extract answers from item exchanges
      setAssessmentAnswers((prev) => {
        const answers: Record<string, AnswerResponse> = { ...prev };
        assessmentExchanges.forEach((exchange) => {
          if (!exchange.itemExchanges) return;
          exchange.itemExchanges.forEach((itemExchange) => {
            const itemId = itemExchange.assessmentItemId;
            if (!itemId) return;
            if (hasSubmittedValue(answers[itemId])) return;

            const answerMarked = itemExchange.answerMarked;
            const submittedValue = answerMarked?.submittedValue;
            const hasSubmittedValueValue = submittedValue !== undefined && submittedValue !== null && submittedValue !== '';
            const hasDisplayValue = answerMarked?.displayValue !== undefined && answerMarked?.displayValue !== '';

            if (answerMarked && hasSubmittedValueValue) {
              answers[itemId] = answerMarked;
              return;
            }

            if (!hasSubmittedValueValue) {
              const assessmentItem = getAssessmentItemById(itemId);
              const suggestedAnswer = assessmentItem
                ? getSuggestedAnswer(assessmentItem, itemExchange)
                : undefined;
              if (suggestedAnswer) {
                answers[itemId] = suggestedAnswer;
                return;
              }
            }

            if (answerMarked && hasDisplayValue) {
              answers[itemId] = answerMarked;
            }
          });
        });
        return answers;
      });
    }
  }, [assessmentExchanges, assessments, person]);

  useEffect(() => {
    if (!person || assessments.length === 0 || selectedAssessmentIds.length === 0) return;

    setAssessmentAnswers((prev) => {
      let changed = false;
      const next: Record<string, AnswerResponse> = { ...prev };

      selectedAssessmentIds.forEach((assessmentId) => {
        const assessment = getSelectedAssessment(assessmentId);
        if (!assessment?.items) return;
        assessment.items.forEach((item) => {
          const profileKey = getProfileKeyForLookup(item);
          if (!profileKey) return;
          if (hasSubmittedValue(next[item.id])) return;
          const suggested = getSuggestedAnswer(item);
          if (suggested) {
            next[item.id] = suggested;
            changed = true;
          }
        });
      });

      return changed ? next : prev;
    });
  }, [person, assessments, selectedAssessmentIds]);

  /**
   * Toggle assessment selection
   * When unselecting, remove all answers for that assessment's items
   */
  const handleAssessmentToggle = (assessmentId: string) => {
    setSelectedAssessmentIds((prev) => {
      if (prev.includes(assessmentId)) {
        // Remove assessment and clean up its answers
        const assessment = assessments.find((a) => a.id === assessmentId);
        const newAnswers = { ...assessmentAnswers };
        if (assessment?.items) {
          assessment.items.forEach((item) => {
            delete newAnswers[item.id];
          });
        }
        setAssessmentAnswers(newAnswers);
        return prev.filter((id) => id !== assessmentId);
      } else {
        return [...prev, assessmentId];
      }
    });
  };

  /**
   * Update answer for a specific assessment item
   */
  const handleAnswerChange = (itemId: string, answer: AnswerResponse) => {
    setAssessmentAnswers((prev) => ({
      ...prev,
      [itemId]: answer,
    }));

    // Clear error for this item when answer changes
    if (itemErrors[itemId]) {
      setItemErrors((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  /**
   * Get selected assessment by ID
   */
  const getSelectedAssessment = (assessmentId: string): BaseAssessment | undefined => {
    return assessments.find((a) => a.id === assessmentId);
  };

  /**
   * Get answer for a specific assessment item
   */
  const getItemAnswer = (itemId: string): AnswerResponse | undefined => {
    return assessmentAnswers[itemId];
  };

  /**
   * Determine input type based on answerType and available answers
   */
  const getInputType = (item: BaseAssessmentItem): InputType => {
    const answers = item.answers || [];
    const answerType = item.answerType?.toLowerCase() || '';

    if (answerType === 'bool' || answerType === 'boolean') {
      return 'bool';
    }

    if (answerType === 'checklist') {
      return 'checkList';
    }

    if (answerType === 'int' || answerType === 'double' || answerType === 'number' || answerType === 'numeric') {
      return 'number';
    }

    // No answers -> text input
    if (answers.length === 0) {
      return 'string';
    }

    // Multiple answers -> checklist
    if (answers.length > 1) {
      return 'checkList';
    }

    // Interval rule implies numeric input
    if (item.rules?.intervalAnswers && Object.keys(item.rules.intervalAnswers).length > 0) {
      return 'number';
    }

    // Default to text
    return 'string';
  };

  /**
   * Validate number inputs against min/max rules
   */
  const validateNumberAnswers = (): { isValid: boolean; errorMessage?: string } => {
    for (const assessmentId of selectedAssessmentIds) {
      const assessment = getSelectedAssessment(assessmentId);
      if (!assessment?.items) continue;

      for (const item of assessment.items) {
        if (!isItemVisible(assessment, item)) continue;

        const inputType = getInputType(item);
        if (inputType !== 'number') continue;

        const answer = getItemAnswer(item.id);
        if (!answer) continue;

        const intervalRule = item.rules?.intervalAnswers?.[0];
        const minValue = intervalRule?.min;
        const maxValue = intervalRule?.max;

        if (minValue === undefined && maxValue === undefined) continue;

        const numValue = answer.submittedValue || answer.displayValue;
        if (!numValue) continue;

        const numValueParsed = parseFloat(numValue);
        if (isNaN(numValueParsed)) {
          return {
            isValid: false,
            errorMessage: `Valoarea pentru "${item.question || 'întrebare'}" nu este un număr valid`,
          };
        }

        if (minValue !== undefined && numValueParsed < minValue) {
          return {
            isValid: false,
            errorMessage: `Valoarea pentru "${item.question || 'întrebare'}" trebuie să fie >= ${minValue}`,
          };
        }

        if (maxValue !== undefined && numValueParsed > maxValue) {
          return {
            isValid: false,
            errorMessage: `Valoarea pentru "${item.question || 'întrebare'}" trebuie să fie <= ${maxValue}`,
          };
        }
      }
    }
    return { isValid: true };
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Validate at least one assessment is selected
    if (selectedAssessmentIds.length === 0) {
      toast.error('Selectați cel puțin o evaluare');
      return;
    }

    if (!sessionId) {
      toast.error('Sesiunea nu este validă');
      return;
    }

    // Validate number inputs
    const validation = validateNumberAnswers();
    if (!validation.isValid) {
      toast.error(validation.errorMessage || 'Validare eșuată');
      return;
    }

    // Prepare request
    const visibleItemIds = new Set<string>();
    selectedAssessmentIds.forEach((assessmentId) => {
      const assessment = getSelectedAssessment(assessmentId);
      if (!assessment?.items) return;
      assessment.items.forEach((item) => {
        if (isItemVisible(assessment, item)) {
          visibleItemIds.add(item.id);
        }
      });
    });

    const filteredAnswers = Object.entries(assessmentAnswers).reduce<Record<string, AnswerResponse>>(
      (acc, [itemId, answer]) => {
        if (visibleItemIds.has(itemId)) {
          acc[itemId] = answer;
        }
        return acc;
      },
      {},
    );

    const request: Step2Request = {
      sessionId: sessionId,
      assessmentIds: selectedAssessmentIds,
      assessmentAnswers: Object.keys(filteredAnswers).length > 0 ? filteredAnswers : undefined,
    };

    // Clear previous errors
    setItemErrors({});

    try {
      const result = await processStep2Mutation.mutateAsync(request);
      if (result.isSuccess && result.data) {
        onSuccess(result.data.sessionId);
      } else {
        // Handle backend validation errors
        const errorMessage = result.errorMessage || '';

        if (errorMessage) {
          // Parse error to identify which item has the error
          // Pattern: "Valoarea pentru întrebarea \"{question}\" ..." or "întrebarea \"{question}\""
          // Try multiple patterns to catch different error message formats
          const questionMatch =
            errorMessage.match(/(?:întrebarea|pentru întrebarea) "([^"]+)"/) ||
            errorMessage.match(/întrebarea "([^"]+)"/);

          if (questionMatch && questionMatch[1]) {
            const questionText = questionMatch[1];

            // Find the item with this question text
            let itemWithError: BaseAssessmentItem | undefined;
            for (const assessmentId of selectedAssessmentIds) {
              const assessment = getSelectedAssessment(assessmentId);
              if (assessment?.items) {
                itemWithError = assessment.items.find((item) => item.question === questionText);
                if (itemWithError) {
                  break;
                }
              }
            }

            if (itemWithError) {
              setItemErrors({ [itemWithError.id]: errorMessage });
              // Scroll to the error item
              setTimeout(() => {
                // Try to find the input element first
                const errorInput =
                  document.getElementById(`${itemWithError!.id}-text`) ||
                  document.getElementById(`${itemWithError!.id}-number`) ||
                  document.getElementById(`${itemWithError!.id}-checkbox`);

                // If input found, scroll to it
                if (errorInput) {
                  errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  errorInput.focus();
                } else {
                  // Otherwise, try to find the question container by data attribute
                  const questionContainer = document.querySelector(`[data-item-id="${itemWithError!.id}"]`);
                  if (questionContainer) {
                    questionContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }
              }, 100);
            } else {
              // If we can't find the specific item, show a general error
              toast.error(errorMessage);
            }
          } else {
            // Error message doesn't contain a question reference, show general error
            toast.error(errorMessage);
          }
        }
      }
    } catch (error) {
      // Error is handled by the hook's onError
      console.error('Error submitting Step2:', error);
    }
  };

  /**
   * Render input component based on type
   */
  const renderItemInput = (item: BaseAssessmentItem) => {
    const inputType = getInputType(item);
    const currentAnswer = getItemAnswer(item.id);
    const itemError = itemErrors[item.id];
    const hasError = !!itemError;

    switch (inputType) {
      case 'bool': {
        // Boolean - SingleSelectChecklist component
        const options = (item.answers ?? []).map((opt, idx) => ({
          key: opt.displayValue || `Opțiune ${idx + 1}`,
          displayValue: opt.displayValue || `Opțiune ${idx + 1}`,
        }));

        const answerKey = currentAnswer?.submittedValue || currentAnswer?.displayValue || '';
        const selectedKey =
          answerKey && options.some((o) => o.key === answerKey) ? answerKey
            : '';

        return (
          <SingleSelectChecklist
            options={options}
            selectedKey={selectedKey}
            onSelectionChange={(key, selectedOption) => {
              handleAnswerChange(item.id, {
                displayValue: selectedOption.displayValue,
                submittedValue: selectedOption.displayValue,
              });
            }}
            hasError={hasError}
            errorMessage={itemError}
          />
        );
      }

      case 'number': {
        // Number input with min/max validation
        const intervalRule = item.rules?.intervalAnswers?.[0];
        const minValue = intervalRule?.min;
        const maxValue = intervalRule?.max;

        const numValue = currentAnswer?.submittedValue || currentAnswer?.displayValue || '';
        const numValueParsed = numValue ? parseFloat(numValue) : NaN;
        const isValid =
          numValue === '' ||
          (!isNaN(numValueParsed) &&
            (minValue === undefined || numValueParsed >= minValue) &&
            (maxValue === undefined || numValueParsed <= maxValue));

        return (
          <div className='space-y-2'>
            <Input
              type='number'
              id={`${item.id}-number`}
              value={numValue}
              onChange={(e) => {
                const newValue = e.target.value;
                handleAnswerChange(item.id, {
                  displayValue: newValue,
                  submittedValue: newValue,
                });
              }}
              placeholder={
                minValue !== undefined && maxValue !== undefined
                  ? `Introduceți un număr între ${minValue} și ${maxValue}`
                  : minValue !== undefined
                    ? `Introduceți un număr >= ${minValue}`
                    : maxValue !== undefined
                      ? `Introduceți un număr <= ${maxValue}`
                      : 'Introduceți un număr'
              }
              min={minValue}
              max={maxValue}
              className={`max-w-xs ${!isValid && numValue !== '' ? 'border-destructive' : ''} ${hasError ? 'border-destructive' : ''}`}
            />
            {!isValid && numValue !== '' && (
              <p className='text-sm text-destructive'>
                {minValue !== undefined && maxValue !== undefined
                  ? `Valoarea trebuie să fie între ${minValue} și ${maxValue}`
                  : minValue !== undefined
                    ? `Valoarea trebuie să fie >= ${minValue}`
                    : maxValue !== undefined
                      ? `Valoarea trebuie să fie <= ${maxValue}`
                      : 'Valoare invalidă'}
              </p>
            )}
            {hasError && !(!isValid && numValue !== '') && <p className='text-sm text-destructive'>{itemError}</p>}
          </div>
        );
      }

      case 'checkList': {
        // Checklist - SingleSelectChecklist component
        const options = (item.answers ?? []).map((opt, idx) => ({
          key: opt.displayValue || `Opțiune ${idx + 1}`,
          displayValue: opt.displayValue || `Opțiune ${idx + 1}`,
        }));

        const answerKey = currentAnswer?.submittedValue || currentAnswer?.displayValue || '';
        const selectedKey =
          answerKey && options.some((o) => o.key === answerKey) ? answerKey
            : '';

        return (
          <SingleSelectChecklist
            options={options}
            selectedKey={selectedKey}
            onSelectionChange={(key, selectedOption) => {
              handleAnswerChange(item.id, {
                displayValue: selectedOption.displayValue,
                submittedValue: selectedOption.displayValue,
              });
            }}
            hasError={hasError}
            errorMessage={itemError}
          />
        );
      }

      case 'string':
      default: {
        // Text input
        return (
          <div className='space-y-2'>
            <Input
              type='text'
              id={`${item.id}-text`}
              value={currentAnswer?.submittedValue || currentAnswer?.displayValue || ''}
              onChange={(e) => {
                const textValue = e.target.value;
                handleAnswerChange(item.id, {
                  displayValue: textValue,
                  submittedValue: textValue,
                });
              }}
              placeholder='Introduceți răspunsul'
              className={`max-w-xs ${hasError ? 'border-destructive' : ''}`}
            />
            {hasError && <p className='text-sm text-destructive'>{itemError}</p>}
          </div>
        );
      }
    }
  };

  // Loading state
  if (isLoadingAssessments) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h2 className='text-2xl font-bold'>Selectați Evaluările</h2>
        <p className='text-muted-foreground mt-2'>
          Selectați evaluările pe care doriți să le completați pentru această sesiune clinică.
        </p>
      </div>

      {/* Assessments List */}
      {assessments.length === 0 ? (
        <Alert>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>Nu există evaluări disponibile.</AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Assessment Selection Card */}
          <Card>
            <CardHeader>
              <CardTitle>Evaluări Disponibile</CardTitle>
              <CardDescription>Selectați cel puțin o evaluare pentru a continua</CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  className='flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors'
                >
                  <Checkbox
                    id={`assessment-${assessment.id}`}
                    checked={selectedAssessmentIds.includes(assessment.id)}
                    onCheckedChange={() => handleAssessmentToggle(assessment.id)}
                  />
                  <div className='flex-1'>
                    <Label htmlFor={`assessment-${assessment.id}`} className='text-base font-medium cursor-pointer'>
                      {assessment.name || 'Evaluare fără nume'}
                    </Label>
                    {assessment.specialization && (
                      <Badge variant='secondary' className='ml-2'>
                        {assessment.specialization}
                      </Badge>
                    )}
                    {assessment.version && (
                      <Badge variant='outline' className='ml-2'>
                        v{assessment.version}
                      </Badge>
                    )}
                    {assessment.items && assessment.items.length > 0 && (
                      <p className='text-sm text-muted-foreground mt-1'>{assessment.items.length} întrebări</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Assessment Items and Answers */}
          {selectedAssessmentIds.length > 0 && (
            <div className='space-y-6'>
              <h3 className='text-xl font-semibold'>Răspundeți la Întrebări</h3>

              {selectedAssessmentIds.map((assessmentId) => {
                const assessment = getSelectedAssessment(assessmentId);
                if (!assessment || !assessment.items || assessment.items.length === 0) {
                  return null;
                }

                // Sort items by orderIndex
                const sortedItems = [...assessment.items].sort((a, b) => {
                  const aIndex = a.orderIndex ?? 0;
                  const bIndex = b.orderIndex ?? 0;
                  return aIndex - bIndex;
                });

                return (
                  <Card key={assessmentId}>
                    <CardHeader>
                      <CardTitle className='flex items-center gap-2'>
                        <ClipboardList className='h-5 w-5' />
                        {assessment.name || 'Evaluare'}
                      </CardTitle>
                      {assessment.specialization && <CardDescription>{assessment.specialization}</CardDescription>}
                    </CardHeader>
                    <CardContent className='space-y-6'>
                      {sortedItems
                        .filter((item) => isItemVisible(assessment, item))
                        .map((item) => {
                        const hasItemError = !!itemErrors[item.id];
                        return (
                          <div
                            key={item.id}
                            data-item-id={item.id}
                            className={`space-y-3 p-4 border rounded-lg transition-colors ${
                              hasItemError ? 'border-destructive bg-destructive/5' : ''
                            }`}
                          >
                            <div className='flex items-start justify-between'>
                              <div className='flex-1'>
                                <Label className={`text-base font-medium ${hasItemError ? 'text-destructive' : ''}`}>
                                  {item.orderIndex && `${item.orderIndex}. `}
                                  {item.question || 'Întrebare fără text'}
                                </Label>
                              </div>
                            </div>
                            {renderItemInput(item)}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex items-center justify-between pt-6'>
            <Button variant='outline' onClick={onBack} disabled={processStep2Mutation.isPending}>
              Înapoi
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedAssessmentIds.length === 0 || processStep2Mutation.isPending}
            >
              {processStep2Mutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Se procesează...
                </>
              ) : (
                'Continuă'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
